import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { ClipboardList, Plus, Download, Search, Filter } from 'lucide-react';
import { toast } from 'sonner';

const META = {
  Draft:      { bg: 'bg-slate-100 text-slate-700 border-slate-200' },
  Issued:     { bg: 'bg-blue-100 text-blue-800 border-blue-200' },
  InProgress: { bg: 'bg-amber-100 text-amber-800 border-amber-200' },
  Completed:  { bg: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  Closed:     { bg: 'bg-slate-900 text-white border-slate-900' },
  Canceled:   { bg: 'bg-red-100 text-red-800 border-red-200' },
};

const ProductionOrdersPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [pos, setPos] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [customerFilter, setCustomerFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (customerFilter !== 'all') params.customer_id = customerFilter;
      const qs = new URLSearchParams(params).toString();
      const [p, c] = await Promise.all([
        api().get(`/production-orders${qs ? `?${qs}` : ''}`),
        api().get('/customers'),
      ]);
      setPos(p.data || []); setCustomers(c.data || []);
    } catch { toast.error('Failed to load production orders'); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, customerFilter]);

  const bulkCsv = async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (customerFilter !== 'all') params.customer_id = customerFilter;
      const qs = new URLSearchParams(params).toString();
      const res = await api().get(`/production-orders/export.csv${qs ? `?${qs}` : ''}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url; a.download = `production-orders-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Bulk CRM export downloaded');
    } catch { toast.error('Export failed'); }
  };

  const filtered = pos.filter(p => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (p.po_number || '').toLowerCase().includes(q)
      || (p.customer_name_snapshot || '').toLowerCase().includes(q)
      || (p.sales_order_number_snapshot || '').toLowerCase().includes(q);
  });

  const stats = pos.reduce((acc, p) => { acc[p.status] = (acc[p.status] || 0) + 1; return acc; }, {});

  return (
    <div className="space-y-4" data-testid="production-orders-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2"><ClipboardList className="w-7 h-7 text-slate-700" /> Production Orders</h1>
          <p className="text-sm text-slate-500 mt-1">Customer-facing manufacturing commitments. Each PO wraps 1+ shop-floor Work Orders.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={bulkCsv} className="rounded-sm" data-testid="po-bulk-csv-btn"><Download className="w-4 h-4 mr-1" /> Bulk CRM Export</Button>
          <Button onClick={() => navigate('/orders/production/new')} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="po-create-btn"><Plus className="w-4 h-4 mr-1" /> New Production Order</Button>
        </div>
      </div>

      <div className="grid grid-cols-6 gap-2">
        {Object.keys(META).map(k => (
          <Card key={k} className="industrial-card"><CardContent className="p-3 text-center">
            <div className="text-2xl font-bold">{stats[k] || 0}</div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-1">{k}</div>
          </CardContent></Card>
        ))}
      </div>

      <Card className="industrial-card">
        <CardHeader className="border-b border-slate-100 pb-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <CardTitle className="text-lg">All Production Orders ({filtered.length})</CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative w-56">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search PO#, customer, SO…" className="pl-8 rounded-sm" data-testid="po-search" />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40 rounded-sm" data-testid="po-status-filter"><Filter className="w-3.5 h-3.5 mr-1" /><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">All statuses</SelectItem>{Object.keys(META).map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={customerFilter} onValueChange={setCustomerFilter}>
                <SelectTrigger className="w-48 rounded-sm" data-testid="po-customer-filter"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">All customers</SelectItem>{customers.map(c => <SelectItem key={c.id} value={c.id}>{c.customer_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? <div className="p-6 text-sm text-slate-500">Loading…</div>
            : filtered.length === 0 ? (
              <div className="p-12 text-center text-slate-500">
                <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="font-semibold">No Production Orders yet</p>
                <p className="text-xs mt-1">Create one from scratch — or go to <Link to="/orders/sales" className="underline">Sales Orders</Link> and click "Spawn Production Order" on any Issued SO.</p>
              </div>
            ) : (
              <Table>
                <TableHeader><TableRow>
                  <TableHead>PO #</TableHead><TableHead>Customer</TableHead><TableHead>Linked SO</TableHead>
                  <TableHead>Priority</TableHead><TableHead>Status</TableHead><TableHead>Lines</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {filtered.map(p => {
                    const m = META[p.status] || META.Draft;
                    return (
                      <TableRow key={p.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/orders/production/${p.id}`)} data-testid={`po-row-${p.po_number}`}>
                        <TableCell className="font-semibold mono">{p.po_number}</TableCell>
                        <TableCell>{p.customer_name_snapshot}</TableCell>
                        <TableCell className="text-xs mono">{p.sales_order_number_snapshot || <span className="text-slate-400">—</span>}</TableCell>
                        <TableCell className="text-xs">{p.priority}</TableCell>
                        <TableCell><Badge className={`${m.bg} border rounded-sm text-xs`}>{p.status}</Badge></TableCell>
                        <TableCell className="text-xs">{(p.lines || []).length}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ProductionOrdersPage;
